import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDismissableMenu } from './useDismissableMenu';

/**
 * A nav item that opens a small menu of further destinations.
 *
 * For places worth reaching that are not one of the three catalogues
 * the app is really about. Putting them here keeps Movies, TV Shows and
 * Autographs as the whole of the nav, rather than making a fourth thing
 * look like a peer of them.
 *
 * Shares the header's menu styles and dismissal behaviour with the user
 * menu, so the two open, close and sit the same way - including opening
 * upwards on a phone, where the nav is fixed to the bottom.
 */

interface INavMenuItem {
  to: string;
  label: string;
  /** A bootstrap icon class, matching the user menu's items. */
  icon: string;
  /**
   * Run before the link is followed, for a destination that needs
   * putting back to its starting state rather than just arriving at.
   */
  onSelect?: () => void;
}

interface INavMenuProps {
  label: string;
  items: INavMenuItem[];
}

export const NavMenu = ({ label, items }: INavMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  useDismissableMenu(open, () => setOpen(false), containerRef);

  // Marked active while you are on one of the pages it leads to, the
  // same as the nav links beside it - otherwise nothing in the header
  // says where you are once you follow it.
  const isActive = items.some((item) => location.pathname.startsWith(item.to));

  return (
    <div className="header-menu" ref={containerRef}>
      <div
        className={`media-a navlink header-menu__trigger${
          isActive ? ' active' : ''
        }`}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
        <i
          className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'} header-menu__caret`}
        ></i>
      </div>
      {open && (
        <div className="header-menu__panel" role="menu">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="header-menu__item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onSelect?.();
              }}
            >
              <i className={`bi ${item.icon}`}></i> {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
